import { FC, useState } from 'react';
import { captureException } from '@sentry/react';
import { useQueryClient } from '@tanstack/react-query';
import { Avatar, Button, Col, Row } from 'antd';
import { TbInfoCircle, TbSparkles, TbWorld } from 'react-icons/tb';
import { toast } from '@/shared/components/toasts/PlatformToast';

import { useAntDesignTheme } from '@/hooks/useAntDesignTheme';
import { logoDevQueryOptions, normalizeLogoDevDomain } from '@/queries';
import type { LogoDevBrand } from '@/queries';
import { CompanyDomainAutocomplete, PlatformForm as Form } from '@/shared/components/forms';
import type { CompanyDomainSelection } from '@/shared/components/forms';
import { EnrichedTooltip } from '@/shared/components/tooltips';

import WhitelabelFieldLabel from '../WhitelabelFieldLabel';
import WhitelabelSectionCard from '../WhitelabelSectionCard';

interface WhitelabelBrandImportCardProps {
    disabled?: boolean;
    onChange: (website: string) => void;
    onClear: () => void;
    onImported: (brand: NonNullable<LogoDevBrand>, domain: string) => void;
    onRevert?: () => void;
    updated?: boolean;
    website: string;
}

const WhitelabelBrandImportCard: FC<WhitelabelBrandImportCardProps> = ({
    disabled = false,
    onChange,
    onClear,
    onImported,
    onRevert,
    updated = false,
    website,
}): JSX.Element => {
    const { token } = useAntDesignTheme();
    const queryClient = useQueryClient();
    const [importing, setImporting] = useState(false);
    const [selectedCompany, setSelectedCompany] = useState<CompanyDomainSelection | null>(null);

    const handleImport = async () => {
        const domain = normalizeLogoDevDomain(website);

        if (!domain || !domain.includes('.')) {
            toast.error('Enter a valid company website before importing its brand.');
            return;
        }

        setImporting(true);

        try {
            const brand = await queryClient.fetchQuery(logoDevQueryOptions.brand(domain));

            if (!brand) {
                toast.info('Brand profile is not available yet', {
                    description: 'Brand information for this website may still be processing. Try again later.',
                });
                return;
            }

            onImported(brand, domain);
            toast.success('Brand theme imported', {
                description: 'Review the generated light and dark theme values, then save your changes.',
            });
        } catch (error) {
            captureException(error);
            toast.error('Brand theme could not be imported.');
        } finally {
            setImporting(false);
        }
    };

    return (
        <WhitelabelSectionCard
            description='Start with your company website to automatically prepare brand colors, identity, and profile details.'
            icon={<TbSparkles size='1rem' style={{ color: token.colorTextTertiary, verticalAlign: '-0.15rem' }} />}
            title='Import Brand Theme'
        >
            <Form.Item
                label={
                    <WhitelabelFieldLabel label='Company Website' onRevert={onRevert} updated={updated}>
                        Company Website
                        <EnrichedTooltip
                            description='Enter the public company website to prepare an editable workspace palette, brand name, logo, and social links.'
                            label='Import Brand Theme'
                            learnMoreTopic='workspace whitelabeling'
                            visual={
                                <div
                                    style={{
                                        backgroundColor: token.colorBgContainer,
                                        border: `1px solid ${token.colorBorder}`,
                                        borderRadius: token.borderRadius,
                                        padding: '0.75rem',
                                        width: '100%',
                                    }}
                                >
                                    <Row align='middle' gutter={[8, 8]} wrap={false}>
                                        <Col flex='none'>
                                            <Avatar
                                                icon={<TbWorld size='1rem' />}
                                                shape='square'
                                                style={{ backgroundColor: token.colorPrimary }}
                                            />
                                        </Col>
                                        <Col flex='auto'>
                                            <strong style={{ color: token.colorText }}>example.com</strong>
                                            <small style={{ color: token.colorTextTertiary, display: 'block' }}>
                                                Brand profile found
                                            </small>
                                        </Col>
                                    </Row>
                                    <div style={{ display: 'flex', gap: '0.375rem', marginTop: '0.75rem' }}>
                                        {[token.colorPrimary, token.colorInfo, token.colorSuccess].map((color) => (
                                            <span
                                                key={color}
                                                style={{
                                                    backgroundColor: color,
                                                    border: `1px solid ${token.colorBorder}`,
                                                    borderRadius: token.borderRadiusSM,
                                                    height: '1.25rem',
                                                    width: '100%',
                                                }}
                                            />
                                        ))}
                                    </div>
                                </div>
                            }
                        >
                            <TbInfoCircle
                                aria-hidden='true'
                                size='0.875rem'
                                style={{ color: token.colorTextTertiary, marginLeft: '0.25rem' }}
                            />
                        </EnrichedTooltip>
                    </WhitelabelFieldLabel>
                }
                style={{ marginBottom: 0 }}
            >
                <Row gutter={[8, 8]} wrap={false}>
                    <Col flex='auto'>
                        <CompanyDomainAutocomplete
                            ariaLabel='Company website'
                            disabled={disabled || importing}
                            placeholder='Search By Company Name Or Website...'
                            selectedCompany={selectedCompany?.domain === website ? selectedCompany : null}
                            selectionValue='domain'
                            value={website}
                            onChange={(value) => {
                                onChange(value);

                                if (selectedCompany && value !== selectedCompany.domain) {
                                    setSelectedCompany(null);
                                }
                            }}
                            onClear={() => {
                                setSelectedCompany(null);
                                onClear();
                            }}
                            onSelect={setSelectedCompany}
                        />
                    </Col>
                    <Col flex='none'>
                        <Button
                            disabled={disabled || !website.trim()}
                            icon={<TbSparkles size='1rem' />}
                            loading={importing}
                            onClick={() => void handleImport()}
                        >
                            Import Brand
                        </Button>
                    </Col>
                </Row>
            </Form.Item>
        </WhitelabelSectionCard>
    );
};

export default WhitelabelBrandImportCard;
